import React from 'react';

/**
 * Ilustrações de fundo por tema dos "Campos de Estudo" do Ryan — traço branco
 * translúcido sobre a cor sólida do card/header, no mesmo espírito do
 * HeaderPattern.tsx do GRAIA (não reaproveitado diretamente: este arquivo é
 * exclusivo do Ryan e cobre um catálogo de temas diferente — sistemas do corpo
 * humano e tipos de prova, em vez de matérias do ENEM).
 *
 * Todo motivo é construído a partir de primitivas SVG previsíveis (circle,
 * ellipse, rect, polygon, linhas retas) ou de traçados verificados (coração e
 * gota são os path exatos do Lucide "Heart"/"Droplet"), evitando curvas
 * livres complexas que deformam em manchas irreconhecíveis.
 *
 * O mesmo tema aparece em 3 contextos com proporções bem diferentes — card do
 * Início (pequeno, quase quadrado), header do campo (faixa larga e baixa) e
 * Hero da página pública de venda (faixa larga e alta, bem maior). Pra evitar
 * cortes/aglomeração, cada tema pode declarar uma composição própria por
 * contexto (`PatternWrap` aceita um `viewBox` custom); temas que não
 * precisam disso continuam usando o viewBox padrão de sempre, sem alteração.
 */

const STROKE_PROPS = {
  fill: 'none',
  stroke: '#fff',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const HEART_PATH =
  'M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.8 1-1a5.5 5.5 0 0 0 0-7.6Z';
const DROPLET_PATH =
  'M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 11.1 5 13 5 15a7 7 0 0 0 7 7Z';
const EYE_PATH = 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z';
const SEARCH_CIRCLE = { cx: 11, cy: 11, r: 8 };
const SEARCH_HANDLE = 'm21 21-4.3-4.3';

export type RyanFieldPatternSize = 'card' | 'header' | 'hero';

// 'card' = canvas lógico maior (mesmas coordenadas absolutas dos desenhos)
// pra reduzir o "zoom" do slice em contêineres pequenos (grid do Início,
// miniaturas do seletor de tema) sem redesenhar nenhum path. 'header' e
// 'hero' herdam o mesmo canvas de sempre — o Hero fica visualmente maior
// só porque o contêiner físico dele é bem maior, sem precisar de outro
// viewBox (o SVG escala junto com o contêiner).
const PatternSizeContext = React.createContext<RyanFieldPatternSize>('header');

const LEGACY_VIEWBOX: Record<RyanFieldPatternSize, string> = {
  card: '0 0 460 345',
  header: '0 0 400 300',
  hero: '0 0 400 300',
};

function PatternWrap({ children, viewBox }: { children: React.ReactNode; viewBox?: string }) {
  const size = React.useContext(PatternSizeContext);
  return (
    <svg viewBox={viewBox ?? LEGACY_VIEWBOX[size]} preserveAspectRatio="xMidYMid slice">
      <g {...STROKE_PROPS}>{children}</g>
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Formas reaproveitáveis (OSCE / Tutoria) — cada uma centrada na própria
// origem local, posicionada via transform translate/scale/rotate no ponto
// de uso. Mantém as composições por contexto legíveis (só coordenadas de
// posição) em vez de repetir path a path em cada variação.
// ─────────────────────────────────────────────────────────────────────────

function Stethoscope({ x, y, scale = 1, opacity = 0.16 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <circle cx="-14" cy="-36" r="4" />
      <circle cx="14" cy="-36" r="4" />
      <path d="M-14 -32q0 22 14 22t14-22" />
      <path d="M0 -10c0 20-14 14-14 34s14 14 14 30" />
      <circle cx="0" cy="54" r="13" />
      <circle cx="0" cy="54" r="5.5" />
    </g>
  );
}

function ReflexHammer({ x, y, scale = 1, rotate = -28, opacity = 0.16 }: { x: number; y: number; scale?: number; rotate?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${rotate}) scale(${scale})`} opacity={opacity}>
      <path d="M0 0 L-24 -15 L-24 15 Z" />
      <path d="M0 0 L48 0" />
      <circle cx="55" cy="0" r="6" />
    </g>
  );
}

function Thermometer({ x, y, scale = 1, opacity = 0.15 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <rect x="-5" y="-46" width="10" height="52" rx="5" />
      <circle cx="0" cy="10" r="11" />
      <path d="M9 -34h6M9 -22h6M9 -10h6" />
    </g>
  );
}

function Clipboard({ x, y, scale = 1, opacity = 0.16 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <rect x="-30" y="0" width="60" height="80" rx="6" />
      <path d="M-12 -16h24v14h-24z" />
      <path d="M-16 24h34M-16 38h34M-16 52h24" />
    </g>
  );
}

function PatientFigure({ x, y, scale = 1, opacity = 0.16 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <circle cx="0" cy="0" r="9" />
      <path d="M0 9c0 12 20 12 20 40" />
      <circle cx="20" cy="60" r="18" />
    </g>
  );
}

function MedCross({ x, y, scale = 1, opacity = 0.14 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <path d="M0 -15v30M-15 0h30" />
    </g>
  );
}

function ClassroomTable({ x, y, rx = 90, ry = 24, opacity = 0.14 }: { x: number; y: number; rx?: number; ry?: number; opacity?: number }) {
  return <ellipse cx={x} cy={y} rx={rx} ry={ry} opacity={opacity} />;
}

function Student({ x, y, scale = 1, opacity = 0.15 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <circle cx="0" cy="0" r="8" />
      <path d="M-16 26q16-18 32 0" />
    </g>
  );
}

function Chalkboard({ x, y, w = 96, h = 68, opacity = 0.13 }: { x: number; y: number; w?: number; h?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y})`} opacity={opacity}>
      <rect x="0" y="0" width={w} height={h} rx="4" />
      <path d="M14 18q9-8 18 0t18 0t18 0t18 0" />
      <path d="M14 34q7-6 14 0t14 0t14 0t14 0t14 0" />
      <circle cx="26" cy="51" r="6" />
      <path d="M34 47 L50 43M34 51 L52 51M34 55 L48 59" />
    </g>
  );
}

function OpenBook({ x, y, scale = 1, opacity = 0.14 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <path d="M0 0c-14-6-28-6-36 0v38c8-6 22-6 36 0c14-6 28-6 36 0v-38c-8-6-22-6-36 0Z" />
      <path d="M0 0v38" />
    </g>
  );
}

function Pencil({ x, y, scale = 1, rotate = -32, opacity = 0.13 }: { x: number; y: number; scale?: number; rotate?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${rotate}) scale(${scale})`} opacity={opacity}>
      <path d="M-42 -5h66v10h-66z" />
      <path d="M24 -5 L42 0 L24 5 Z" />
    </g>
  );
}

function GraduationCapShape({ x, y, scale = 1, opacity = 0.15 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <path d="M-38 -6 L0 -24 L38 -6 L0 12 Z" />
      <path d="M-20 2v18q20 12 40 0v-18" />
      <path d="M38 -6v18" />
    </g>
  );
}

// ── Sistemas do corpo — formas reaproveitáveis (cardio/neuro/digestório/
// urinário/musculoesquelético/imunológico/hematológico/sentidos) ──────────

function HeartOutline({ x, y, scale = 1, rotate = 0, opacity = 0.15 }: { x: number; y: number; scale?: number; rotate?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${rotate}) scale(${scale})`} opacity={opacity}>
      <path d={HEART_PATH} transform="translate(-12,-12)" />
      <path d="M0 -4v9M-5 0h10" />
    </g>
  );
}

function EkgLine({ x, y, scale = 1, opacity = 0.18 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return <path d="M-60 0h20l8-22l14 40l10-30l8 12h20" opacity={opacity} transform={`translate(${x} ${y}) scale(${scale})`} />;
}

function Vessel({ x, y, scale = 1, rotate = 0, opacity = 0.15 }: { x: number; y: number; scale?: number; rotate?: number; opacity?: number }) {
  return (
    <path
      d="M0 -40 V0 M0 -14 L-16 4 M0 -14 L16 4 M0 10 V40 M0 20 L14 34"
      opacity={opacity}
      transform={`translate(${x} ${y}) rotate(${rotate}) scale(${scale})`}
    />
  );
}

function LungLobe({ x, y, scale = 1, rotate = 0, opacity = 0.16 }: { x: number; y: number; scale?: number; rotate?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${rotate}) scale(${scale})`} opacity={opacity}>
      <ellipse cx="0" cy="0" rx="34" ry="52" />
      <path d="M-16 -18h32M-18 0h32M-14 18h28" />
    </g>
  );
}

function Trachea({ x, y, scale = 1, opacity = 0.2 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return <path d="M0 -34v34M0 0l-32 18M0 0l32 18" opacity={opacity} transform={`translate(${x} ${y}) scale(${scale})`} />;
}

function BreathWave({ x, y, scale = 1, opacity = 0.16 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return <path d="M-90 0q26-13 52 0t52 0t52 0t52 0t52 0t52 0" opacity={opacity} transform={`translate(${x} ${y}) scale(${scale})`} />;
}

function Neuron({ x, y, scale = 1, opacity = 0.16 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <circle cx="0" cy="0" r="10" />
      <path d="M-8 -6 L-24 -16M-9 0 L-28 0M-8 6 L-24 16" />
      <path d="M9 -3 L32 -14M10 3 L34 10" />
    </g>
  );
}

function BrainOutline({ x, y, scale = 1, opacity = 0.14 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <path d="M-30 0c0-16 14-24 30-24s30 8 30 24s-14 26-30 26s-30-10-30-26Z" />
      <path d="M-18 -14q6-6 12 0M0 -16q6-6 12 0M-14 4q6-6 12 0M4 2q6-6 12 0" />
    </g>
  );
}

function Synapse({ x, y, scale = 1, opacity = 0.16 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <circle cx="-14" cy="0" r="8" />
      <circle cx="14" cy="0" r="8" />
      <path d="M-4 -3l3 3l-2 2l3 3M2 -3l-3 3l2 2l-3 3" />
    </g>
  );
}

// Estômago — blob orgânico em "J" com 2 dobras internas (rugae) + um tubinho
// vertical curto na base sugerindo a saída pro duodeno. A saída é
// deliberadamente um traço RETO/paralelo (mesma linguagem visual do tubo do
// esôfago), não um traço diagonal solto: testado lado a lado, um traço
// diagonal saindo de um blob lê como lupa (ícone de busca), não como órgão —
// esse era o defeito da versão anterior.
function StomachOrgan({ x, y, scale = 1, rotate = 0, opacity = 0.16 }: { x: number; y: number; scale?: number; rotate?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${rotate}) scale(${scale})`} opacity={opacity}>
      <path d="M-8 -34C10-34 24-22 24-6C24 10 14 24-4 26C-20 28-32 16-30 0C-29-10-24-20-14-28C-12-30-10-32-8-34Z" />
      <path d="M4 -12q10 2 14 12" />
      <path d="M-14 0q10 4 16 14" />
      <path d="M18 22v14M24 20v14" />
    </g>
  );
}

// Alça intestinal — espiral compacta (novelo), a única forma das 3 que já lia
// bem sozinha nos testes; só engrossei o traço pra não sumir perto do resto
// da composição.
function Intestine({ x, y, scale = 1, opacity = 0.14 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <path
      d="M-26 0a26 15 0 1 1 52 0a18 10 0 1 1-36 0a11 6 0 1 1 22 0a5 3 0 1 1-10 0"
      opacity={opacity}
      strokeWidth={2.6}
      transform={`translate(${x} ${y}) scale(${scale})`}
    />
  );
}

// Fígado — UM blob só (não dois círculos separados, que liam como "duas
// bolhas soltas" nos testes anteriores) com 2 linhas internas curtas
// sugerindo a divisão entre os lobos — textura sem quebrar a silhueta em
// pedaços.
function LiverOrgan({ x, y, scale = 1, opacity = 0.14 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <path d="M-30 4C-32-10-18-24 0-24C20-24 34-8 30 8C27 20 14 26-2 24C-18 22-28 16-30 4Z" />
      <path d="M4 -20c8 6 12 16 8 26" />
      <path d="M-14 -14c4 8 4 18 0 26" />
    </g>
  );
}

// Tubo do esôfago — 2 linhas paralelas com leve ondulação (peristalse),
// pensado pra encaixar bem acima do StomachOrgan na composição do tema.
function EsophagusTube({ x, y, scale = 1, opacity = 0.18 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <path
      d="M-6 -38c-3 9 3 15 0 22c-2 8 2 13 0 20M6 -38c3 9-3 15 0 22c2 8-2 13 0 20"
      opacity={opacity}
      transform={`translate(${x} ${y}) scale(${scale})`}
    />
  );
}

// Xícara de café — corpo afunilado fechado + alça + fumaça, pra remeter a
// gatilhos alimentares clássicos do digestório (refluxo, gastrite) sem
// depender só de órgãos internos.
function CoffeeCup({ x, y, scale = 1, opacity = 0.16 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <path d="M-16 -8L14 -8L10 26Q10 32 4 32L-10 32Q-16 32-16 26Z" />
      <path d="M14 -2c14-2 14 20 0 18" />
      <path d="M-6 -14q-6-10 0-20M6 -14q-6-10 0-20" />
    </g>
  );
}

// Rim em feijão com hilo bem marcado (chanfro medial côncavo, de onde sai a
// pelve renal/ureter) — `mirror` espelha o chanfro pro outro lado, pra usar
// em par (os dois rins com a concavidade voltada um pro outro, como no
// desenho de referência do sistema renal).
function Kidney({ x, y, scale = 1, rotate = 0, mirror = false, opacity = 0.15 }: { x: number; y: number; scale?: number; rotate?: number; mirror?: boolean; opacity?: number }) {
  const sx = mirror ? -scale : scale;
  return (
    <g transform={`translate(${x} ${y}) rotate(${rotate}) scale(${sx} ${scale})`} opacity={opacity}>
      <path d="M2 -30C22-30 34-16 34 0C34 16 22 30 2 30C-8 30-14 22-10 14C-7 8-7-8-10-14C-14-22-8-30 2-30Z" />
      {/* pelve renal — pequena bifurcação no hilo, de onde nasce o ureter */}
      <path d="M-8 -8c6 4 6 12 0 16" />
    </g>
  );
}

// Par de ureteres — duas linhas saindo do hilo de cada rim e convergindo
// numa só antes da bexiga/copo, a "ligação" do sistema urinário (mesmo
// desenho do ícone de referência: rim → ureter → bexiga/copo).
function UreterPair({ leftFrom, rightFrom, to, opacity = 0.16 }: { leftFrom: [number, number]; rightFrom: [number, number]; to: [number, number]; opacity?: number }) {
  const [lx, ly] = leftFrom;
  const [rx, ry] = rightFrom;
  const [tx, ty] = to;
  const my = (ly + ty) / 2;
  return (
    <path
      d={`M${lx} ${ly}Q${lx} ${my} ${tx} ${ty}M${rx} ${ry}Q${rx} ${my} ${tx} ${ty}`}
      opacity={opacity}
    />
  );
}

// Copo de amostra de urina — mesmo corpo afunilado do CoffeeCup (ver acima),
// trocando alça/fumaça por aba/tampa na boca + linha de nível marcando a
// amostra, pra remeter direto ao copinho de exame de urina (EAS/urocultura).
function UrineCup({ x, y, scale = 1, opacity = 0.14 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <path d="M-18 -10L18 -10L14 30Q14 36 8 36L-8 36Q-14 36-14 30Z" />
      <path d="M-22 -10h44" />
      <path d="M-14 12h28" />
    </g>
  );
}

function LongBone({ x, y, scale = 1, rotate = 0, opacity = 0.16 }: { x: number; y: number; scale?: number; rotate?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${rotate}) scale(${scale})`} opacity={opacity}>
      <rect x="-6" y="-40" width="12" height="80" rx="6" />
      <circle cx="-8" cy="-40" r="9" /><circle cx="8" cy="-40" r="9" />
      <circle cx="-8" cy="40" r="9" /><circle cx="8" cy="40" r="9" />
    </g>
  );
}

function JointSimple({ x, y, scale = 1, rotate = 0, opacity = 0.15 }: { x: number; y: number; scale?: number; rotate?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${rotate}) scale(${scale})`} opacity={opacity}>
      <rect x="-9" y="-46" width="18" height="36" rx="7" />
      <rect x="-9" y="10" width="18" height="36" rx="7" />
      <path d="M-11 -6h22" />
    </g>
  );
}

function SpineSegment({ x, y, scale = 1, rotate = 0, opacity = 0.14 }: { x: number; y: number; scale?: number; rotate?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${rotate}) scale(${scale})`} opacity={opacity}>
      <rect x="-12" y="-40" width="24" height="16" rx="4" />
      <rect x="-12" y="-20" width="24" height="16" rx="4" />
      <rect x="-12" y="0" width="24" height="16" rx="4" />
      <rect x="-12" y="20" width="24" height="16" rx="4" />
      <path d="M12 -32 L22 -32M12 -12 L22 -12M12 8 L22 8M12 28 L22 28" />
    </g>
  );
}

function Antibody({ x, y, scale = 1, rotate = 0, opacity = 0.16 }: { x: number; y: number; scale?: number; rotate?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${rotate}) scale(${scale})`} opacity={opacity}>
      <path d="M-16 -28 L0 -6 L16 -28M0 -6 V30" />
      <path d="M-16 -28 L-24 -34M-16 -28 L-22 -20M16 -28 L24 -34M16 -28 L22 -20" />
    </g>
  );
}

function Lymphocyte({ x, y, scale = 1, opacity = 0.15 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <circle cx="0" cy="0" r="20" />
      <path d="M-8 -6c4-6 12-6 14 0c4 6-2 12-8 12c-8 0-10-6-6-12Z" />
    </g>
  );
}

function Shield({ x, y, scale = 1, opacity = 0.15 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <path
      d="M0 -26 L20 -18 V4 C20 20 8 30 0 34 C-8 30 -20 20 -20 4 V-18Z"
      opacity={opacity}
      transform={`translate(${x} ${y}) scale(${scale})`}
    />
  );
}

function RedBloodCell({ x, y, scale = 1, opacity = 0.15 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <circle cx="0" cy="0" r="16" />
      <circle cx="0" cy="0" r="6" />
    </g>
  );
}

function TestTubeShape({ x, y, scale = 1, rotate = 0, opacity = 0.15 }: { x: number; y: number; scale?: number; rotate?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${rotate}) scale(${scale})`} opacity={opacity}>
      <path d="M-10 -36h20M-8 -36v40a8 8 0 0 0 16 0v-40" />
      <path d="M-8 -6h16" />
    </g>
  );
}

function EarProfile({ x, y, scale = 1, opacity = 0.15 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <path
      d="M-4 -20c14-4 24 6 24 18c0 10-8 18-18 18c-6 0-10-4-10-9c0-4 3-6 6-6c4 0 6 3 6 6"
      opacity={opacity}
      transform={`translate(${x} ${y}) scale(${scale})`}
    />
  );
}

function NoseProfile({ x, y, scale = 1, opacity = 0.14 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <path
      d="M-10 -22 C-4 -22 2 -6 10 4c4 4 2 10-4 10h-6"
      opacity={opacity}
      transform={`translate(${x} ${y}) scale(${scale})`}
    />
  );
}

function SkinLayers({ x, y, scale = 1, opacity = 0.13 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <path d="M-42 0q10-14 21 0t21 0t21 0t21 0" />
      <path d="M-42 20q10-14 21 0t21 0t21 0t21 0" />
      <path d="M-42 40q10-14 21 0t21 0t21 0t21 0" />
    </g>
  );
}

function SunRays({ x, y, scale = 1, opacity = 0.16 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <circle cx="0" cy="0" r="12" />
      <path d="M0 -22v8M0 14v8M-22 0h8M14 0h8M-14 -14l5.5 5.5M8.5 8.5l5.5 5.5M-14 14l5.5-5.5M8.5-8.5l5.5-5.5" />
    </g>
  );
}

function VenusSymbol({ x, y, scale = 1, opacity = 0.15 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <circle cx="0" cy="-10" r="16" />
      <path d="M0 6v22M-11 21h22" />
    </g>
  );
}

function MarsSymbol({ x, y, scale = 1, opacity = 0.15 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <circle cx="-8" cy="10" r="16" />
      <path d="M4 -2 L27 -25M27 -25h-16M27 -25v16" />
    </g>
  );
}

function BabyOutline({ x, y, scale = 1, opacity = 0.16 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <circle cx="0" cy="-18" r="13" />
      <path d="M-15 -2c0 20 30 20 30 0" />
      <path d="M-17 6q-8 8 -5 18M17 6q8 8 5 18" />
    </g>
  );
}

// ── Provas — formas reaproveitáveis (prova geral/métodos científicos/iESC/
// integradas/programada) ───────────────────────────────────────────────

function ExamSheet({ x, y, scale = 1, opacity = 0.13 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <rect x="0" y="0" width="70" height="86" rx="5" />
      <path d="M12 18 L20 26 L34 10" />
      <path d="M44 18h16" />
      <path d="M12 40 L20 48 L34 32" />
      <path d="M44 40h16" />
      <path d="M12 62 L20 70 L34 54" />
      <path d="M44 62h16" />
    </g>
  );
}

function ClockSimple({ x, y, scale = 1, opacity = 0.15 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <circle cx="0" cy="0" r="20" />
      <path d="M0 0v-12M0 0l9 6" />
    </g>
  );
}

function CheckMark({ x, y, scale = 1, rotate = 0, opacity = 0.18 }: { x: number; y: number; scale?: number; rotate?: number; opacity?: number }) {
  return <path d="M-10 0 L-2 9 L14 -12" opacity={opacity} transform={`translate(${x} ${y}) rotate(${rotate}) scale(${scale})`} />;
}

function Magnifier({ x, y, scale = 1, opacity = 0.15 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <circle cx={SEARCH_CIRCLE.cx} cy={SEARCH_CIRCLE.cy} r={SEARCH_CIRCLE.r} />
      <path d={SEARCH_HANDLE} />
    </g>
  );
}

function GraphCurve({ x, y, scale = 1, opacity = 0.14 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <path d="M-30 20h60M-30 20v-50" />
      <path d="M-26 14q10-4 16-14t14-16t14 2" />
    </g>
  );
}

function DnaHelix({ x, y, scale = 1, opacity = 0.15 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <path d="M-10 -40q20 20 0 40M10 -40q-20 20 0 40" />
      <path d="M-9 -26h18M-10 -6h20M-9 14h18" />
    </g>
  );
}

function HouseSimple({ x, y, scale = 1, opacity = 0.15 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <path d="M-24 0 L0 -22 L24 0" />
      <path d="M-16 0v28h32V0" />
      <path d="M-4 28v-16h8v16" />
    </g>
  );
}

function MedicalBag({ x, y, scale = 1, opacity = 0.15 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <path d="M-10 -30h20v10h-20z" />
      <rect x="-26" y="-20" width="52" height="40" rx="6" />
      <path d="M0 -8v16M-8 0h16" />
    </g>
  );
}

function HealthPost({ x, y, scale = 1, opacity = 0.14 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <path d="M-22 2 L0 -18 L22 2" />
      <path d="M-14 2v24h28V2" />
      <path d="M0 8v12M-6 14h12" />
    </g>
  );
}

function Calendar({ x, y, scale = 1, opacity = 0.14 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <rect x="-26" y="-20" width="52" height="46" rx="5" />
      <path d="M-26 -6h52" />
      <path d="M-16 -26v12M16 -26v12" />
      <rect x="-8" y="6" width="12" height="10" rx="2" />
    </g>
  );
}

function ChecklistSimple({ x, y, scale = 1, opacity = 0.13 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <path d="M-20 -20 L-14 -14 L-4 -26" />
      <path d="M6 -20h24" />
      <path d="M-20 2 L-14 8 L-4 -4" />
      <path d="M6 2h24" />
      <path d="M-20 24 L-14 30 L-4 18" />
      <path d="M6 24h24" />
    </g>
  );
}

function GearSimple({ x, y, scale = 1, opacity = 0.14 }: { x: number; y: number; scale?: number; opacity?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <circle cx="0" cy="0" r="14" />
      <circle cx="0" cy="0" r="5" />
      <path d="M0 -14v-8M0 14v8M-14 0h-8M14 0h8M-10 -10l-6-6M10 -10l6-6M-10 10l-6 6M10 10l6 6" />
    </g>
  );
}

function PuzzlePiece({ x, y, scale = 1, rotate = 0, opacity = 0.15 }: { x: number; y: number; scale?: number; rotate?: number; opacity?: number }) {
  return (
    <path
      d="M-20 -20h16a6 6 0 0 1 12 0h16v16a6 6 0 0 1 0 12v16h-16a6 6 0 0 1-12 0h-16v-16a6 6 0 0 1 0-12Z"
      opacity={opacity}
      transform={`translate(${x} ${y}) rotate(${rotate}) scale(${scale})`}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Temas — "Sistemas do corpo"
// ─────────────────────────────────────────────────────────────────────────

// Geral — tema neutro/fallback: livro aberto + capelo de formatura + cruz
// médica, remetendo a estudo médico em geral (sem ligação a um sistema ou
// formato de prova específico). Elementos deliberadamente diferentes dos
// usados em Prova/OSCE/Tutoria (mesmo grupo "provas") pra não duplicar visual.
const DEFAULT_VIEWBOX: Record<RyanFieldPatternSize, string> = {
  card: '0 0 460 345',
  header: '0 0 1000 220',
  hero: '0 0 1200 460',
};

const PatternDefault: React.FC = () => {
  const size = React.useContext(PatternSizeContext);

  if (size === 'card') {
    return (
      <PatternWrap viewBox={DEFAULT_VIEWBOX.card}>
        <OpenBook x={160} y={190} scale={1.5} opacity={0.15} />
        <GraduationCapShape x={320} y={110} scale={1.2} opacity={0.15} />
        <MedCross x={340} y={260} scale={1} opacity={0.13} />
        <circle cx="70" cy="70" r="3" opacity="0.16" />
      </PatternWrap>
    );
  }

  if (size === 'hero') {
    return (
      <PatternWrap viewBox={DEFAULT_VIEWBOX.hero}>
        <OpenBook x={210} y={200} scale={2.1} opacity={0.15} />
        <GraduationCapShape x={560} y={110} scale={1.8} opacity={0.15} />
        <MedCross x={800} y={220} scale={1.5} opacity={0.13} />
        <Stethoscope x={1030} y={170} scale={1.3} opacity={0.13} />
        <circle cx="100" cy="380" r="4" opacity="0.13" />
        <circle cx="330" cy="60" r="2.6" opacity="0.11" />
      </PatternWrap>
    );
  }

  return (
    <PatternWrap viewBox={DEFAULT_VIEWBOX.header}>
      <OpenBook x={180} y={120} scale={1.6} opacity={0.15} />
      <GraduationCapShape x={430} y={70} scale={1.3} opacity={0.15} />
      <MedCross x={630} y={140} scale={1.1} opacity={0.13} />
      <Stethoscope x={830} y={110} scale={0.9} opacity={0.13} />
      <circle cx="120" cy="190" r="2.4" opacity="0.12" />
    </PatternWrap>
  );
};

const CARDIO_VIEWBOX: Record<RyanFieldPatternSize, string> = {
  card: '0 0 460 345',
  header: '0 0 1000 220',
  hero: '0 0 1200 460',
};

const PatternCardiovascular: React.FC = () => {
  const size = React.useContext(PatternSizeContext);

  if (size === 'card') {
    return (
      <PatternWrap viewBox={CARDIO_VIEWBOX.card}>
        <HeartOutline x={150} y={190} scale={3.2} opacity={0.15} />
        <EkgLine x={330} y={130} scale={1} opacity={0.2} />
        <Vessel x={350} y={260} scale={0.9} rotate={12} opacity={0.14} />
        <circle cx="70" cy="80" r="3" opacity="0.16" />
      </PatternWrap>
    );
  }

  if (size === 'hero') {
    return (
      <PatternWrap viewBox={CARDIO_VIEWBOX.hero}>
        <HeartOutline x={170} y={190} scale={4.2} opacity={0.16} />
        <EkgLine x={440} y={150} scale={1.6} opacity={0.2} />
        <Vessel x={660} y={290} scale={1.3} rotate={12} opacity={0.14} />
        <HeartOutline x={920} y={130} scale={2.2} rotate={-8} opacity={0.11} />
        <EkgLine x={1080} y={310} scale={1.1} opacity={0.15} />
        <circle cx="90" cy="360" r="4" opacity="0.13" />
        <circle cx="300" cy="60" r="3" opacity="0.12" />
      </PatternWrap>
    );
  }

  return (
    <PatternWrap viewBox={CARDIO_VIEWBOX.header}>
      <HeartOutline x={130} y={110} scale={3.4} opacity={0.16} />
      <EkgLine x={340} y={110} scale={1.3} opacity={0.2} />
      <Vessel x={530} y={130} scale={1} rotate={8} opacity={0.13} />
      <HeartOutline x={760} y={150} scale={1.7} rotate={10} opacity={0.11} />
      <EkgLine x={900} y={80} scale={0.9} opacity={0.15} />
      <circle cx="200" cy="40" r="2.6" opacity="0.12" />
    </PatternWrap>
  );
};

const RESPIRA_VIEWBOX: Record<RyanFieldPatternSize, string> = {
  card: '0 0 460 345',
  header: '0 0 1000 220',
  hero: '0 0 1200 460',
};

const PatternRespiratorio: React.FC = () => {
  const size = React.useContext(PatternSizeContext);

  if (size === 'card') {
    return (
      <PatternWrap viewBox={RESPIRA_VIEWBOX.card}>
        <Trachea x={230} y={60} scale={1.15} opacity={0.2} />
        <LungLobe x={182} y={150} scale={1.05} rotate={-14} opacity={0.16} />
        <LungLobe x={278} y={150} scale={1.05} rotate={14} opacity={0.16} />
        <BreathWave x={230} y={290} scale={0.85} opacity={0.16} />
        <circle cx="350" cy="70" r="3" opacity="0.18" />
      </PatternWrap>
    );
  }

  if (size === 'hero') {
    return (
      <PatternWrap viewBox={RESPIRA_VIEWBOX.hero}>
        <Trachea x={230} y={110} scale={1.5} opacity={0.2} />
        <LungLobe x={160} y={230} scale={1.55} rotate={-14} opacity={0.16} />
        <LungLobe x={300} y={230} scale={1.55} rotate={14} opacity={0.16} />
        <BreathWave x={680} y={330} scale={1.7} opacity={0.16} />
        <Trachea x={980} y={90} scale={0.9} opacity={0.15} />
        <LungLobe x={935} y={155} scale={1} rotate={-14} opacity={0.11} />
        <LungLobe x={1030} y={155} scale={1} rotate={14} opacity={0.11} />
        <circle cx="110" cy="380" r="4" opacity="0.13" />
      </PatternWrap>
    );
  }

  return (
    <PatternWrap viewBox={RESPIRA_VIEWBOX.header}>
      <Trachea x={430} y={45} scale={1} opacity={0.2} />
      <LungLobe x={380} y={110} scale={1.05} rotate={-14} opacity={0.16} />
      <LungLobe x={480} y={110} scale={1.05} rotate={14} opacity={0.16} />
      <BreathWave x={750} y={150} scale={1.1} opacity={0.16} />
      <LungLobe x={870} y={90} scale={0.65} rotate={-10} opacity={0.1} />
      <circle cx="180" cy="180" r="2.4" opacity="0.12" />
    </PatternWrap>
  );
};

const NEURO_VIEWBOX: Record<RyanFieldPatternSize, string> = {
  card: '0 0 460 345',
  header: '0 0 1000 220',
  hero: '0 0 1200 460',
};

const PatternNeurologico: React.FC = () => {
  const size = React.useContext(PatternSizeContext);

  if (size === 'card') {
    return (
      <PatternWrap viewBox={NEURO_VIEWBOX.card}>
        <BrainOutline x={260} y={140} scale={1.5} opacity={0.14} />
        <Neuron x={130} y={250} scale={1.3} opacity={0.16} />
        <Synapse x={350} y={270} scale={1} opacity={0.15} />
      </PatternWrap>
    );
  }

  if (size === 'hero') {
    return (
      <PatternWrap viewBox={NEURO_VIEWBOX.hero}>
        <BrainOutline x={230} y={150} scale={2.1} opacity={0.14} />
        <Neuron x={520} y={110} scale={1.7} opacity={0.16} />
        <Synapse x={780} y={220} scale={1.4} opacity={0.15} />
        <Neuron x={980} y={330} scale={1.3} opacity={0.13} />
        <BrainOutline x={1080} y={100} scale={1} opacity={0.1} />
        <circle cx="100" cy="380" r="4" opacity="0.13" />
      </PatternWrap>
    );
  }

  return (
    <PatternWrap viewBox={NEURO_VIEWBOX.header}>
      <BrainOutline x={200} y={110} scale={1.6} opacity={0.14} />
      <Neuron x={430} y={80} scale={1.3} opacity={0.16} />
      <Synapse x={620} y={150} scale={1.1} opacity={0.15} />
      <Neuron x={830} y={110} scale={1} opacity={0.12} />
      <circle cx="120" cy="180" r="2.4" opacity="0.12" />
    </PatternWrap>
  );
};

const DIGEST_VIEWBOX: Record<RyanFieldPatternSize, string> = {
  card: '0 0 460 345',
  header: '0 0 1000 220',
  hero: '0 0 1200 460',
};

const PatternDigestorio: React.FC = () => {
  const size = React.useContext(PatternSizeContext);

  if (size === 'card') {
    return (
      <PatternWrap viewBox={DIGEST_VIEWBOX.card}>
        <EsophagusTube x={150} y={75} scale={1.1} opacity={0.18} />
        <StomachOrgan x={150} y={155} scale={1.2} opacity={0.16} />
        <Intestine x={310} y={260} scale={1.1} opacity={0.14} />
        <LiverOrgan x={330} y={95} scale={0.95} opacity={0.13} />
        <CoffeeCup x={70} y={275} scale={1.3} opacity={0.13} />
      </PatternWrap>
    );
  }

  if (size === 'hero') {
    return (
      <PatternWrap viewBox={DIGEST_VIEWBOX.hero}>
        <EsophagusTube x={190} y={100} scale={1.5} opacity={0.18} />
        <StomachOrgan x={190} y={225} scale={1.6} opacity={0.16} />
        <Intestine x={420} y={330} scale={1.6} opacity={0.16} />
        <LiverOrgan x={780} y={150} scale={1.3} opacity={0.13} />
        <CoffeeCup x={1050} y={340} scale={1.8} opacity={0.13} />
        <StomachOrgan x={1040} y={130} scale={0.7} rotate={-14} opacity={0.1} />
        <circle cx="90" cy="80" r="3" opacity="0.12" />
      </PatternWrap>
    );
  }

  return (
    <PatternWrap viewBox={DIGEST_VIEWBOX.header}>
      <EsophagusTube x={150} y={45} scale={0.85} opacity={0.18} />
      <StomachOrgan x={150} y={120} scale={1.05} opacity={0.16} />
      <Intestine x={300} y={160} scale={1.15} opacity={0.16} />
      <LiverOrgan x={640} y={90} scale={0.95} opacity={0.13} />
      <CoffeeCup x={860} y={145} scale={1.1} opacity={0.13} />
      <circle cx="900" cy="55" r="2.2" opacity="0.11" />
    </PatternWrap>
  );
};

const URIN_VIEWBOX: Record<RyanFieldPatternSize, string> = {
  card: '0 0 460 345',
  header: '0 0 1000 220',
  hero: '0 0 1200 460',
};

const PatternUrinario: React.FC = () => {
  const size = React.useContext(PatternSizeContext);

  if (size === 'card') {
    // O card usa preserveAspectRatio="slice" (cobre o container e corta o
    // excesso, tipo background-size:cover) e o container real é bem mais
    // baixo/largo que o viewBox 460×345 — então só sobrevive ao corte uma
    // faixa vertical curta centrada no meio do viewBox (y≈172). A composição
    // antiga espalhava do rim (y=95) até a base do copo (y≈273), muito além
    // dessa faixa segura — por isso ficava cortada/estranha. Agora tudo fica
    // compacto e centrado em ~172 (rins em y=128, copo em y≈212, base em
    // ≈241), cabendo inteiro mesmo no card mais largo/baixo do grid.
    return (
      <PatternWrap viewBox={URIN_VIEWBOX.card}>
        <Kidney x={175} y={128} scale={0.8} rotate={-5} mirror opacity={0.15} />
        <Kidney x={255} y={128} scale={0.8} rotate={5} opacity={0.15} />
        <UreterPair leftFrom={[183, 148]} rightFrom={[247, 148]} to={[215, 200]} opacity={0.15} />
        {/* Copo deslocado do eixo do ureter (que converge em x=215) — assim
            não passa a impressão de urina caindo direto no copo. */}
        <UrineCup x={155} y={212} scale={0.8} opacity={0.14} />
        <path d={DROPLET_PATH} opacity="0.13" transform="translate(290,195) scale(1.05)" />
      </PatternWrap>
    );
  }

  if (size === 'hero') {
    return (
      <PatternWrap viewBox={URIN_VIEWBOX.hero}>
        <Kidney x={210} y={145} scale={1.7} rotate={-8} mirror opacity={0.15} />
        <Kidney x={342} y={145} scale={1.7} rotate={8} opacity={0.15} />
        <UreterPair leftFrom={[228, 180]} rightFrom={[324, 180]} to={[276, 245]} opacity={0.15} />
        <UrineCup x={276} y={315} scale={1.7} opacity={0.14} />
        <path d={DROPLET_PATH} opacity="0.13" transform="translate(395,240) scale(2)" />
        <Kidney x={990} y={310} scale={1.3} rotate={-16} opacity={0.1} />
        <path d={DROPLET_PATH} opacity="0.12" transform="translate(1080,140) scale(2.2)" />
        <circle cx="110" cy="380" r="4" opacity="0.13" />
      </PatternWrap>
    );
  }

  return (
    <PatternWrap viewBox={URIN_VIEWBOX.header}>
      <Kidney x={148} y={90} scale={1.05} rotate={-8} mirror opacity={0.15} />
      <Kidney x={228} y={90} scale={1.05} rotate={8} opacity={0.15} />
      <UreterPair leftFrom={[158, 108]} rightFrom={[218, 108]} to={[188, 148]} opacity={0.15} />
      <UrineCup x={188} y={183} scale={1.05} opacity={0.14} />
      <path d={DROPLET_PATH} opacity="0.13" transform="translate(258,150) scale(1.4)" />
      <Kidney x={800} y={90} scale={0.85} rotate={-14} opacity={0.1} />
      <path d={DROPLET_PATH} opacity="0.12" transform="translate(900,55) scale(1.6)" />
    </PatternWrap>
  );
};

const MUSC_VIEWBOX: Record<RyanFieldPatternSize, string> = {
  card: '0 0 460 345',
  header: '0 0 1000 220',
  hero: '0 0 1200 460',
};

const PatternMusculoesqueletico: React.FC = () => {
  const size = React.useContext(PatternSizeContext);

  if (size === 'card') {
    return (
      <PatternWrap viewBox={MUSC_VIEWBOX.card}>
        <LongBone x={300} y={90} rotate={35} scale={1} opacity={0.16} />
        <JointSimple x={150} y={240} rotate={-20} scale={0.9} opacity={0.14} />
        <SpineSegment x={350} y={260} scale={0.85} opacity={0.13} />
      </PatternWrap>
    );
  }

  if (size === 'hero') {
    return (
      <PatternWrap viewBox={MUSC_VIEWBOX.hero}>
        <LongBone x={200} y={150} rotate={35} scale={1.4} opacity={0.16} />
        <JointSimple x={520} y={120} rotate={-15} scale={1.2} opacity={0.14} />
        <SpineSegment x={780} y={190} scale={1.1} opacity={0.13} />
        <LongBone x={1040} y={310} rotate={35} scale={0.9} opacity={0.1} />
        <circle cx="90" cy="330" r="4" opacity="0.12" />
      </PatternWrap>
    );
  }

  return (
    <PatternWrap viewBox={MUSC_VIEWBOX.header}>
      <LongBone x={150} y={110} rotate={35} scale={1} opacity={0.16} />
      <JointSimple x={390} y={100} rotate={-15} scale={0.9} opacity={0.14} />
      <SpineSegment x={610} y={130} scale={0.85} opacity={0.13} />
      <LongBone x={860} y={150} rotate={35} scale={0.65} opacity={0.1} />
    </PatternWrap>
  );
};

const SENT_VIEWBOX: Record<RyanFieldPatternSize, string> = {
  card: '0 0 460 345',
  header: '0 0 1000 220',
  hero: '0 0 1200 460',
};

const PatternSentidos: React.FC = () => {
  const size = React.useContext(PatternSizeContext);

  // Só 2 dos 3 (olho/orelha/nariz) no card pra não poluir; header e hero têm
  // espaço de sobra pros 3 juntos, bem espalhados.
  if (size === 'card') {
    return (
      <PatternWrap viewBox={SENT_VIEWBOX.card}>
        <g transform="translate(280,90) scale(4.2)" opacity="0.15">
          <path d={EYE_PATH} />
          <circle cx="12" cy="12" r="3" />
        </g>
        <EarProfile x={140} y={250} scale={1.6} opacity={0.14} />
      </PatternWrap>
    );
  }

  if (size === 'hero') {
    return (
      <PatternWrap viewBox={SENT_VIEWBOX.hero}>
        <g transform="translate(220,140) scale(5.6)" opacity="0.15">
          <path d={EYE_PATH} />
          <circle cx="12" cy="12" r="3" />
        </g>
        <EarProfile x={620} y={180} scale={2.4} opacity={0.14} />
        <NoseProfile x={920} y={230} scale={2.2} opacity={0.13} />
        <circle cx="110" cy="360" r="4" opacity="0.12" />
      </PatternWrap>
    );
  }

  return (
    <PatternWrap viewBox={SENT_VIEWBOX.header}>
      <g transform="translate(190,100) scale(4.6)" opacity="0.15">
        <path d={EYE_PATH} />
        <circle cx="12" cy="12" r="3" />
      </g>
      <EarProfile x={540} y={130} scale={1.9} opacity={0.14} />
      <NoseProfile x={800} y={160} scale={1.7} opacity={0.13} />
    </PatternWrap>
  );
};

const DERMATO_VIEWBOX: Record<RyanFieldPatternSize, string> = {
  card: '0 0 460 345',
  header: '0 0 1000 220',
  hero: '0 0 1200 460',
};

const PatternDermatologico: React.FC = () => {
  const size = React.useContext(PatternSizeContext);

  if (size === 'card') {
    return (
      <PatternWrap viewBox={DERMATO_VIEWBOX.card}>
        <SunRays x={310} y={70} scale={1.3} opacity={0.16} />
        <SkinLayers x={150} y={210} scale={1.3} opacity={0.13} />
        <Magnifier x={320} y={250} scale={2.6} opacity={0.15} />
        <circle cx="60" cy="120" r="2.4" opacity="0.14" />
        <circle cx="80" cy="135" r="1.8" opacity="0.12" />
      </PatternWrap>
    );
  }

  if (size === 'hero') {
    return (
      <PatternWrap viewBox={DERMATO_VIEWBOX.hero}>
        <SunRays x={190} y={90} scale={1.9} opacity={0.16} />
        <SkinLayers x={560} y={200} scale={2.2} opacity={0.13} />
        <Magnifier x={890} y={240} scale={4.6} opacity={0.15} />
        <SkinLayers x={1080} y={110} scale={1.3} opacity={0.1} />
        <circle cx="100" cy="360" r="4" opacity="0.13" />
        <circle cx="130" cy="380" r="2.6" opacity="0.11" />
      </PatternWrap>
    );
  }

  return (
    <PatternWrap viewBox={DERMATO_VIEWBOX.header}>
      <SunRays x={170} y={60} scale={1.3} opacity={0.16} />
      <SkinLayers x={430} y={130} scale={1.6} opacity={0.13} />
      <Magnifier x={720} y={150} scale={3.4} opacity={0.15} />
      <SkinLayers x={900} y={70} scale={1} opacity={0.1} />
      <circle cx="220" cy="45" r="2.2" opacity="0.12" />
    </PatternWrap>
  );
};

const PatternEndocrino: React.FC = () => (
  <PatternWrap>
    <polygon points="330,40 352,53 352,79 330,92 308,79 308,53" opacity="0.15" />
    <circle cx="330" cy="40" r="4" opacity="0.2" />
    <circle cx="352" cy="79" r="4" opacity="0.2" />
    <circle cx="308" cy="79" r="4" opacity="0.2" />
    <path d="M330 40 L330 20 M352 79 L368 90 M308 79 L292 90" opacity="0.13" />
    <polygon points="70,236 82,243 82,257 70,264 58,257 58,243" opacity="0.1" />
  </PatternWrap>
);

const HEMA_VIEWBOX: Record<RyanFieldPatternSize, string> = {
  card: '0 0 460 345',
  header: '0 0 1000 220',
  hero: '0 0 1200 460',
};

const PatternHematologico: React.FC = () => {
  const size = React.useContext(PatternSizeContext);

  if (size === 'card') {
    return (
      <PatternWrap viewBox={HEMA_VIEWBOX.card}>
        <RedBloodCell x={150} y={160} scale={1.3} opacity={0.15} />
        <path d={DROPLET_PATH} opacity="0.16" transform="translate(300,80) scale(3.2)" />
        <TestTubeShape x={330} y={250} scale={1} opacity={0.14} />
      </PatternWrap>
    );
  }

  if (size === 'hero') {
    return (
      <PatternWrap viewBox={HEMA_VIEWBOX.hero}>
        <RedBloodCell x={190} y={170} scale={1.9} opacity={0.15} />
        <path d={DROPLET_PATH} opacity="0.16" transform="translate(500,90) scale(4.6)" />
        <TestTubeShape x={780} y={230} scale={1.5} opacity={0.14} />
        <RedBloodCell x={1030} y={330} scale={1.3} opacity={0.11} />
        <RedBloodCell x={1080} y={140} scale={0.9} opacity={0.1} />
      </PatternWrap>
    );
  }

  return (
    <PatternWrap viewBox={HEMA_VIEWBOX.header}>
      <RedBloodCell x={150} y={120} scale={1.3} opacity={0.15} />
      <RedBloodCell x={210} y={165} scale={0.9} opacity={0.1} />
      <path d={DROPLET_PATH} opacity="0.16" transform="translate(410,55) scale(3.6)" />
      <TestTubeShape x={630} y={140} scale={1.05} opacity={0.14} />
      <RedBloodCell x={870} y={150} scale={1.1} opacity={0.12} />
    </PatternWrap>
  );
};

const REPRO_VIEWBOX: Record<RyanFieldPatternSize, string> = {
  card: '0 0 460 345',
  header: '0 0 1000 220',
  hero: '0 0 1200 460',
};

const PatternReprodutor: React.FC = () => {
  const size = React.useContext(PatternSizeContext);

  if (size === 'card') {
    return (
      <PatternWrap viewBox={REPRO_VIEWBOX.card}>
        <VenusSymbol x={160} y={140} scale={1.5} opacity={0.15} />
        <MarsSymbol x={300} y={150} scale={1.5} opacity={0.15} />
        <BabyOutline x={230} y={270} scale={1.5} opacity={0.16} />
        <circle cx="70" cy="70" r="3" opacity="0.14" />
      </PatternWrap>
    );
  }

  if (size === 'hero') {
    return (
      <PatternWrap viewBox={REPRO_VIEWBOX.hero}>
        <VenusSymbol x={200} y={140} scale={2.1} opacity={0.15} />
        <MarsSymbol x={430} y={170} scale={2.1} opacity={0.15} />
        <BabyOutline x={660} y={270} scale={2} opacity={0.16} />
        <DnaHelix x={930} y={160} scale={1.7} opacity={0.14} />
        <VenusSymbol x={1090} y={90} scale={1.1} opacity={0.1} />
        <circle cx="110" cy="380" r="4" opacity="0.13" />
      </PatternWrap>
    );
  }

  return (
    <PatternWrap viewBox={REPRO_VIEWBOX.header}>
      <VenusSymbol x={180} y={90} scale={1.5} opacity={0.15} />
      <MarsSymbol x={330} y={110} scale={1.5} opacity={0.15} />
      <BabyOutline x={510} y={150} scale={1.5} opacity={0.16} />
      <DnaHelix x={760} y={110} scale={1.2} opacity={0.14} />
      <MarsSymbol x={900} y={70} scale={0.9} opacity={0.1} />
    </PatternWrap>
  );
};

const IMUNO_VIEWBOX: Record<RyanFieldPatternSize, string> = {
  card: '0 0 460 345',
  header: '0 0 1000 220',
  hero: '0 0 1200 460',
};

const PatternImunologico: React.FC = () => {
  const size = React.useContext(PatternSizeContext);

  if (size === 'card') {
    return (
      <PatternWrap viewBox={IMUNO_VIEWBOX.card}>
        <Antibody x={150} y={160} scale={1.3} opacity={0.16} />
        <Lymphocyte x={330} y={250} scale={1} opacity={0.14} />
        <Shield x={340} y={100} scale={0.9} opacity={0.13} />
      </PatternWrap>
    );
  }

  if (size === 'hero') {
    return (
      <PatternWrap viewBox={IMUNO_VIEWBOX.hero}>
        <Antibody x={200} y={170} scale={1.9} opacity={0.16} />
        <Lymphocyte x={540} y={280} scale={1.5} opacity={0.14} />
        <Shield x={800} y={130} scale={1.4} opacity={0.13} />
        <Lymphocyte x={1040} y={330} scale={1} opacity={0.1} />
        <circle cx="100" cy="100" r="3" opacity="0.12" />
      </PatternWrap>
    );
  }

  return (
    <PatternWrap viewBox={IMUNO_VIEWBOX.header}>
      <Antibody x={150} y={120} scale={1.3} opacity={0.16} />
      <Lymphocyte x={420} y={160} scale={1.1} opacity={0.14} />
      <Shield x={630} y={90} scale={1} opacity={0.13} />
      <Antibody x={860} y={150} scale={0.9} opacity={0.1} />
    </PatternWrap>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Temas — "Provas"
// ─────────────────────────────────────────────────────────────────────────

const PROVA_VIEWBOX: Record<RyanFieldPatternSize, string> = {
  card: '0 0 460 345',
  header: '0 0 1000 220',
  hero: '0 0 1200 460',
};

const PatternProva: React.FC = () => {
  const size = React.useContext(PatternSizeContext);

  if (size === 'card') {
    return (
      <PatternWrap viewBox={PROVA_VIEWBOX.card}>
        <ExamSheet x={140} y={150} scale={1} opacity={0.13} />
        <Pencil x={330} y={100} scale={1} rotate={-32} opacity={0.15} />
        <ClockSimple x={330} y={260} scale={0.9} opacity={0.14} />
      </PatternWrap>
    );
  }

  if (size === 'hero') {
    return (
      <PatternWrap viewBox={PROVA_VIEWBOX.hero}>
        <ExamSheet x={190} y={160} scale={1.5} opacity={0.13} />
        <Pencil x={520} y={100} scale={1.5} rotate={-32} opacity={0.15} />
        <ClockSimple x={780} y={230} scale={1.3} opacity={0.14} />
        <CheckMark x={1020} y={130} scale={1.8} opacity={0.16} />
        <ExamSheet x={1080} y={340} scale={0.9} opacity={0.1} />
      </PatternWrap>
    );
  }

  return (
    <PatternWrap viewBox={PROVA_VIEWBOX.header}>
      <ExamSheet x={140} y={110} scale={1} opacity={0.13} />
      <Pencil x={380} y={70} scale={1} rotate={-32} opacity={0.15} />
      <ClockSimple x={580} y={150} scale={0.95} opacity={0.14} />
      <CheckMark x={800} y={90} scale={1.4} opacity={0.16} />
    </PatternWrap>
  );
};

const INTEGRA_VIEWBOX: Record<RyanFieldPatternSize, string> = {
  card: '0 0 460 345',
  header: '0 0 1000 220',
  hero: '0 0 1200 460',
};

// Integradas — peças de quebra-cabeça se encaixando + engrenagens interligadas,
// remetendo a integração de conhecimentos/disciplinas.
const PatternIntegradora: React.FC = () => {
  const size = React.useContext(PatternSizeContext);

  if (size === 'card') {
    return (
      <PatternWrap viewBox={INTEGRA_VIEWBOX.card}>
        <PuzzlePiece x={140} y={150} scale={1.2} opacity={0.14} />
        <PuzzlePiece x={216} y={150} rotate={180} scale={1.2} opacity={0.14} />
        <GearSimple x={330} y={260} scale={1} opacity={0.13} />
        <GearSimple x={368} y={260} scale={0.75} opacity={0.11} />
      </PatternWrap>
    );
  }

  if (size === 'hero') {
    return (
      <PatternWrap viewBox={INTEGRA_VIEWBOX.hero}>
        <PuzzlePiece x={190} y={170} scale={1.7} opacity={0.14} />
        <PuzzlePiece x={300} y={170} rotate={180} scale={1.7} opacity={0.14} />
        <GearSimple x={650} y={280} scale={1.5} opacity={0.13} />
        <GearSimple x={710} y={280} scale={1.1} opacity={0.11} />
        <PuzzlePiece x={980} y={120} scale={1.1} opacity={0.1} />
        <circle cx="100" cy="380" r="4" opacity="0.12" />
      </PatternWrap>
    );
  }

  return (
    <PatternWrap viewBox={INTEGRA_VIEWBOX.header}>
      <PuzzlePiece x={150} y={110} scale={1.2} opacity={0.14} />
      <PuzzlePiece x={226} y={110} rotate={180} scale={1.2} opacity={0.14} />
      <GearSimple x={520} y={150} scale={1} opacity={0.13} />
      <GearSimple x={560} y={150} scale={0.75} opacity={0.11} />
      <PuzzlePiece x={840} y={90} scale={0.9} opacity={0.1} />
    </PatternWrap>
  );
};

const PROGRAM_VIEWBOX: Record<RyanFieldPatternSize, string> = {
  card: '0 0 460 345',
  header: '0 0 1000 220',
  hero: '0 0 1200 460',
};

// Programada — calendário estilizado + checklist + relógio com engrenagem
// sobreposta (rotina/planejamento).
const PatternProgramada: React.FC = () => {
  const size = React.useContext(PatternSizeContext);

  if (size === 'card') {
    return (
      <PatternWrap viewBox={PROGRAM_VIEWBOX.card}>
        <Calendar x={140} y={140} scale={1.3} opacity={0.15} />
        <ChecklistSimple x={320} y={240} scale={1} opacity={0.13} />
        <ClockSimple x={330} y={90} scale={0.85} opacity={0.15} />
        <GearSimple x={355} y={65} scale={0.5} opacity={0.13} />
      </PatternWrap>
    );
  }

  if (size === 'hero') {
    return (
      <PatternWrap viewBox={PROGRAM_VIEWBOX.hero}>
        <Calendar x={190} y={150} scale={1.9} opacity={0.15} />
        <ChecklistSimple x={540} y={290} scale={1.5} opacity={0.13} />
        <ClockSimple x={820} y={130} scale={1.3} opacity={0.15} />
        <GearSimple x={870} y={90} scale={0.8} opacity={0.13} />
        <Calendar x={1040} y={330} scale={1} opacity={0.1} />
      </PatternWrap>
    );
  }

  return (
    <PatternWrap viewBox={PROGRAM_VIEWBOX.header}>
      <Calendar x={150} y={110} scale={1.3} opacity={0.15} />
      <ChecklistSimple x={410} y={160} scale={1} opacity={0.13} />
      <ClockSimple x={620} y={80} scale={1} opacity={0.15} />
      <GearSimple x={655} y={50} scale={0.55} opacity={0.13} />
      <Calendar x={870} y={150} scale={0.9} opacity={0.1} />
    </PatternWrap>
  );
};

// OSCE — recomposto em 3 variações por contexto, com os elementos espalhados
// pelo espaço inteiro (não só amontoados num canto) e o estetoscópio/martelo/
// termômetro claramente reconhecíveis dentro dos limites do viewBox.
const OSCE_VIEWBOX: Record<RyanFieldPatternSize, string> = {
  card: '0 0 460 345',
  header: '0 0 1000 220',
  hero: '0 0 1200 460',
};

const PatternOsce: React.FC = () => {
  const size = React.useContext(PatternSizeContext);

  if (size === 'card') {
    return (
      <PatternWrap viewBox={OSCE_VIEWBOX.card}>
        <Clipboard x={140} y={205} scale={1.25} opacity={0.16} />
        <Stethoscope x={330} y={110} scale={1.2} opacity={0.17} />
        <MedCross x={380} y={280} scale={0.9} opacity={0.13} />
        <circle cx="70" cy="60" r="3" opacity="0.16" />
      </PatternWrap>
    );
  }

  if (size === 'hero') {
    return (
      <PatternWrap viewBox={OSCE_VIEWBOX.hero}>
        <Stethoscope x={150} y={130} scale={1.6} opacity={0.17} />
        <Clipboard x={340} y={330} scale={1.4} opacity={0.16} />
        <MedCross x={540} y={90} scale={1.3} opacity={0.13} />
        <PatientFigure x={700} y={300} scale={1.2} opacity={0.16} />
        <Thermometer x={870} y={120} scale={1.3} opacity={0.15} />
        <ReflexHammer x={1030} y={300} scale={1.3} opacity={0.16} />
        <circle cx="60" cy="330" r="4" opacity="0.15" />
        <circle cx="1010" cy="70" r="3" opacity="0.14" />
        <circle cx="470" cy="410" r="3" opacity="0.13" />
        <circle cx="780" cy="60" r="2.6" opacity="0.12" />
      </PatternWrap>
    );
  }

  return (
    <PatternWrap viewBox={OSCE_VIEWBOX.header}>
      <Stethoscope x={110} y={95} scale={1.05} opacity={0.16} />
      <Clipboard x={260} y={148} scale={0.85} opacity={0.15} />
      <MedCross x={430} y={55} scale={0.95} opacity={0.12} />
      <PatientFigure x={570} y={135} scale={0.8} opacity={0.15} />
      <Thermometer x={710} y={70} scale={0.95} opacity={0.14} />
      <ReflexHammer x={840} y={150} scale={0.95} opacity={0.15} />
      <circle cx="180" cy="35" r="2.6" opacity="0.13" />
      <circle cx="630" cy="195" r="2.6" opacity="0.12" />
      <circle cx="920" cy="75" r="2.2" opacity="0.12" />
    </PatternWrap>
  );
};

const METODO_VIEWBOX: Record<RyanFieldPatternSize, string> = {
  card: '0 0 460 345',
  header: '0 0 1000 220',
  hero: '0 0 1200 460',
};

const PatternMetodosCientifico: React.FC = () => {
  const size = React.useContext(PatternSizeContext);

  if (size === 'card') {
    return (
      <PatternWrap viewBox={METODO_VIEWBOX.card}>
        <Magnifier x={140} y={140} scale={4} opacity={0.15} />
        <TestTubeShape x={320} y={110} scale={1} opacity={0.15} />
        <GraphCurve x={320} y={250} scale={1} opacity={0.14} />
      </PatternWrap>
    );
  }

  if (size === 'hero') {
    return (
      <PatternWrap viewBox={METODO_VIEWBOX.hero}>
        <Magnifier x={190} y={160} scale={5.4} opacity={0.15} />
        <TestTubeShape x={540} y={120} scale={1.5} opacity={0.15} />
        <GraphCurve x={800} y={280} scale={1.4} opacity={0.14} />
        <DnaHelix x={1040} y={150} scale={1.5} opacity={0.14} />
        <circle cx="120" cy="380" r="4" opacity="0.12" />
      </PatternWrap>
    );
  }

  return (
    <PatternWrap viewBox={METODO_VIEWBOX.header}>
      <Magnifier x={150} y={110} scale={4.2} opacity={0.15} />
      <TestTubeShape x={410} y={80} scale={1.05} opacity={0.15} />
      <GraphCurve x={620} y={160} scale={1} opacity={0.14} />
      <DnaHelix x={850} y={100} scale={1.1} opacity={0.14} />
    </PatternWrap>
  );
};

const IESC_VIEWBOX: Record<RyanFieldPatternSize, string> = {
  card: '0 0 460 345',
  header: '0 0 1000 220',
  hero: '0 0 1200 460',
};

// iESC — casinha (visita domiciliar), maleta médica, posto de saúde
// (casinha+cruz), remetendo a atenção primária/comunidade.
const PatternIesc: React.FC = () => {
  const size = React.useContext(PatternSizeContext);

  if (size === 'card') {
    return (
      <PatternWrap viewBox={IESC_VIEWBOX.card}>
        <HouseSimple x={150} y={160} scale={1.3} opacity={0.15} />
        <MedicalBag x={330} y={250} scale={1} opacity={0.15} />
        <HealthPost x={340} y={100} scale={0.9} opacity={0.13} />
      </PatternWrap>
    );
  }

  if (size === 'hero') {
    return (
      <PatternWrap viewBox={IESC_VIEWBOX.hero}>
        <HouseSimple x={200} y={180} scale={1.9} opacity={0.15} />
        <MedicalBag x={560} y={290} scale={1.5} opacity={0.15} />
        <HealthPost x={830} y={140} scale={1.4} opacity={0.13} />
        <HouseSimple x={1050} y={330} scale={1} opacity={0.1} />
        <circle cx="110" cy="90" r="3" opacity="0.12" />
      </PatternWrap>
    );
  }

  return (
    <PatternWrap viewBox={IESC_VIEWBOX.header}>
      <HouseSimple x={150} y={130} scale={1.3} opacity={0.15} />
      <MedicalBag x={430} y={170} scale={1.05} opacity={0.15} />
      <HealthPost x={650} y={100} scale={1} opacity={0.13} />
      <HouseSimple x={880} y={150} scale={0.85} opacity={0.1} />
    </PatternWrap>
  );
};

// Tutoria — mesa com contorno de alunos ao redor + quadro com anotações
// estilizadas + livro/lápis reforçando o clima de sala de aula. Recomposto
// por contexto pra a mesa nunca ficar cortada (card pequeno = mesa menor e
// bem dentro das margens; header/hero = mesa larga, alunos espalhados ao
// longo de toda a faixa, não amontoados).
const TUTORIA_VIEWBOX: Record<RyanFieldPatternSize, string> = {
  card: '0 0 460 345',
  header: '0 0 1000 220',
  hero: '0 0 1200 460',
};

const PatternTutoria: React.FC = () => {
  const size = React.useContext(PatternSizeContext);

  if (size === 'card') {
    return (
      <PatternWrap viewBox={TUTORIA_VIEWBOX.card}>
        <ClassroomTable x={230} y={230} rx={92} ry={26} opacity={0.14} />
        {/* Estudantes um pouco mais próximos da mesa (mais sensação de "sentados
            ali" do que flutuando acima dela) */}
        <Student x={150} y={178} scale={1} opacity={0.16} />
        <Student x={310} y={178} scale={1} opacity={0.16} />
        <Student x={230} y={148} scale={0.9} opacity={0.13} />
        <Chalkboard x={300} y={26} w={110} h={64} opacity={0.12} />
        <Pencil x={70} y={280} scale={0.85} opacity={0.12} />
      </PatternWrap>
    );
  }

  if (size === 'hero') {
    // Mesma cena coesa do card (mesa + alunos próximos, sem esticar), só que
    // maior — os elementos que enchem o resto da faixa larga são outros
    // acessórios de sala de aula (quadro, livro, lápis), não a mesa esticada.
    //
    // A cena (mesa+alunos) fica deslocada bem pro terço esquerdo, de propósito:
    // o container do Hero (.rpp-hero-pattern) aplica uma máscara radial que
    // apaga quase por completo a região central (onde fica o título/CTA por
    // cima) — colada no centro, a cena inteira ficava invisível. Os outros
    // acessórios (quadro, livro, lápis), sendo elementos soltos e não uma
    // cena única, toleram ficar mais pro centro/direita mesmo esmaecidos.
    return (
      <PatternWrap viewBox={TUTORIA_VIEWBOX.hero}>
        <ClassroomTable x={230} y={330} rx={130} ry={33} opacity={0.15} />
        <Student x={130} y={210} scale={1.25} opacity={0.17} />
        <Student x={230} y={178} scale={1.28} opacity={0.17} />
        <Student x={335} y={210} scale={1.2} opacity={0.16} />
        <Student x={70} y={265} scale={1.05} opacity={0.13} />
        <Chalkboard x={60} y={40} w={200} h={128} opacity={0.14} />
        <OpenBook x={950} y={310} scale={1.7} opacity={0.13} />
        <Pencil x={1060} y={130} scale={1.3} opacity={0.12} />
        <Student x={470} y={270} scale={0.9} opacity={0.1} />
        <circle cx="170" cy="400" r="4" opacity="0.13" />
        <circle cx="1090" cy="380" r="3" opacity="0.12" />
        <circle cx="380" cy="90" r="3" opacity="0.11" />
      </PatternWrap>
    );
  }

  return (
    <PatternWrap viewBox={TUTORIA_VIEWBOX.header}>
      {/* Mesma lógica do Hero: .ryan-field-hero-pattern também mascara o
          centro (onde fica o título do campo) — cena da mesa deslocada pro
          terço esquerdo pra não cair na zona apagada. */}
      <ClassroomTable x={175} y={155} rx={95} ry={24} opacity={0.15} />
      {/* Estudantes um pouco mais próximos da mesa (mesmo ajuste do card) */}
      <Student x={100} y={110} scale={0.95} opacity={0.17} />
      <Student x={175} y={93} scale={0.98} opacity={0.17} />
      <Student x={250} y={110} scale={0.9} opacity={0.15} />
      {/* Quadro deslocado da mesa (que ficava colado nela) — precisa ficar fora
          da faixa quase-central (~x420-580), que a máscara radial do header
          apaga quase por completo; por isso vai mais pra direita, ainda solto
          da mesa mas dentro da região onde a ilustração aparece de verdade. */}
      <Chalkboard x={660} y={22} w={100} h={64} opacity={0.13} />
      <OpenBook x={840} y={125} scale={1.1} opacity={0.13} />
      <Pencil x={920} y={45} scale={0.9} opacity={0.12} />
      <circle cx="150" cy="190" r="2.6" opacity="0.12" />
      <circle cx="360" cy="55" r="2.2" opacity="0.11" />
      <circle cx="950" cy="175" r="2.4" opacity="0.11" />
    </PatternWrap>
  );
};

const PATTERNS: Record<string, React.FC> = {
  default: PatternDefault,
  cardiovascular: PatternCardiovascular,
  respiratorio: PatternRespiratorio,
  neurologico: PatternNeurologico,
  digestorio: PatternDigestorio,
  urinario: PatternUrinario,
  musculoesqueletico: PatternMusculoesqueletico,
  sentidos: PatternSentidos,
  dermatologico: PatternDermatologico,
  endocrino: PatternEndocrino,
  hematologico: PatternHematologico,
  reprodutor: PatternReprodutor,
  imunologico: PatternImunologico,
  prova: PatternProva,
  integradora: PatternIntegradora,
  programada: PatternProgramada,
  osce: PatternOsce,
  metodoscientifico: PatternMetodosCientifico,
  iesc: PatternIesc,
  tutoria: PatternTutoria,
};

interface RyanFieldPatternProps {
  theme?: string;
  /** Contexto físico onde a ilustração aparece — controla proporção/composição. */
  size?: RyanFieldPatternSize;
}

const RyanFieldPattern: React.FC<RyanFieldPatternProps> = ({ theme = 'default', size = 'header' }) => {
  const Pattern = PATTERNS[theme] ?? PatternDefault;
  return (
    <PatternSizeContext.Provider value={size}>
      <Pattern />
    </PatternSizeContext.Provider>
  );
};

export default RyanFieldPattern;
